import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {generatePath, useNavigate, useParams} from "react-router-dom";
import {selectStore} from "../../../store/selector";
import React, {useCallback, useEffect} from "react";
import {
  addQualification,
  deleteQualification,
  deleteRups, editQualification,
  fetchQualifications,
  fetchSpecializationId,
  resetNewRupState,
} from "../../../store/pages/rup/actions";
import {Button, Card, Col, Row, Select, Table, Tooltip} from "antd";
import {DeleteOutlined, EyeOutlined} from "@ant-design/icons";
import {MyButton, MyText} from "../../../components";
import {ROUTE_PATH, RUP_BASE_TYPES} from "../../../constants";

const monthsOptions = [
  {id: 1, time_months: '0'},
  {id: 2, time_months: '1'},
  {id: 3, time_months: '2'},
  {id: 4, time_months: '3'},
  {id: 5, time_months: '4'},
  {id: 6, time_months: '5'},
  {id: 7, time_months: '6'},
  {id: 8, time_months: '7'},
  {id: 9, time_months: '8'},
  {id: 10, time_months: '9'},
  {id: 11, time_months: '10'},
  {id: 12, time_months: '11'},
  {id: 13, time_months: '12'},
]

const yearsOptions = [
  {id: 1, time_years: '0'},
  {id: 2, time_years: '1'},
  {id: 3, time_years: '2'},
  {id: 4, time_years: '3'},
  {id: 5, time_years: '4'},
]

export const QualificationRup = () => {
  const dispatch = useDispatch()
  const {t} = useTranslation('rup')
  const navigate = useNavigate();
  const {rupId} = useParams()

  const {
    rup: {
      currentRup: currentRupState,
      qualifications: qualificationsState,
      qualificationsOptions: qualificationsOptionsState,
    }
  } = useSelector(selectStore)

  useEffect(() => {
    dispatch(fetchSpecializationId(rupId))
    dispatch(fetchQualifications(rupId))
  }, [])


  const columns = [
    {
      key: 'dataIndex',
      title: `${t('table.index')}`,
      dataIndex: 'id',
    },
    {
      title: `${t('table.qualifications')}`,
      width: '400px',
      dataIndex: 'name',
      render: (text: any, record: any, index: any) => (
        <Select key={'qualification_select' + record.id}
                placeholder={t('select.qualification.placeholder')}
                filterOption={(input: any, option: any) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                style={{width: '400px'}}
                onChange={(value, children) => handleChangeRow('name', value, index, record, children)}
                value={record ? record.name : null}
                loading={qualificationsOptionsState.loading}
        >
          {qualificationsOptionsState.data?.map((qualificationOption: any) => (
            <Select.Option key={'qualification_options_' + qualificationOption.id}
                           value={qualificationOption.id}>{qualificationOption.caption + '-' + qualificationOption.code}</Select.Option>
          ))}
        </Select>
      )
    },
    {
      title: `${t('table.normativSrok')}`,
      children: [
        {
          title: `${t('select.year.label')}`,
          dataIndex: 'time_years',
          render: (text: any, record: any, index: any) => (
            <Select key={'years_select' + record.id}
                    placeholder={t('select.year.placeholder')}
                    style={{width: '210px'}}
                    filterOption={(input: any, option: any) =>
                      option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
                      option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    onChange={(value: any, children: any) => handleChangeRow('time_years', value, index, record, children)}
                    value={record?.time_years}
            >
              {yearsOptions.map(year => (
                <Select.Option key={'year_' + year.id} value={year.time_years}>{year.time_years}</Select.Option>
              ))}
            </Select>
          )
        },
        {
          title: `${t('select.month.label')}`,
          dataIndex: 'time_months',
          render: (text: any, record: any, index: any) => (
            <>
              <Select key={'months_select' + record.id}
                      placeholder={t('select.month.placeholder')}
                      style={{width: '210px'}}
                      filterOption={(input: any, option: any) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                      onChange={(value: any, children: any) => handleChangeRow('time_months', value, index, record, children)}
                      value={record?.time_months}
              >
                {monthsOptions.map((month: any) => (
                  <Select.Option key={'month_' + month.id} value={month.time_months}>{month.time_months}</Select.Option>
                ))}
              </Select>
            </>
          )
        },
      ]
    },
    {
      title: `${t('table.action')}`,
      width: 120,
      render: (text: any, record: any, index: any) => (
        <Row justify='center' style={{width: 120}}>
          <Col key={'actions' + record.id} style={{marginRight: '8px'}}>
            <Button type='text'
                    style={{display: 'flex', alignItems: 'center'}}
                    onClick={handleEditHours}
            >
              <EyeOutlined/>
            </Button>
          </Col>
          <Col key={'actions2' + record.id}>
            {
              record.isCanDelete ?
                <Button icon={<DeleteOutlined style={{position: 'relative', color: '#ED1C24'}}/>} onClick={() => handleDeleteQualification(record)}/>
                :
                <Tooltip title={t('error.rupFull')}>
                  <Button icon={<DeleteOutlined style={{position: 'relative', color: '#ED1C24'}}/>} disabled={true}/>
                </Tooltip>
            }
          </Col>
        </Row>
      )
    },
  ]

  const handleBackToRups = useCallback(() => {
    navigate(-1)
    dispatch(resetNewRupState())
  }, [])

  const handleDeleteRup = useCallback(() => {
    dispatch(deleteRups(rupId))

    handleBackToRups()
  }, [])

  const handleCreateRup = useCallback(async () => {
    dispatch(addQualification(rupId))
  }, [])

  const handleChangeRow = useCallback((key: any, value: any, index: any, record: any, children: any) => {
    let body = {
      key: key,
      id: record.id,
      value: value,
      name: children.children
    }

    dispatch(editQualification(body))
  }, [])

  const handleEditHours = useCallback(() => {
    const path = {
      // @ts-ignore
      pathname: generatePath(ROUTE_PATH.RUP_QUALIFICATION_TABLE, {rupId}),
    }

    navigate(path)
  }, [rupId])

  const handleDeleteQualification = useCallback((record: any) => {
    dispatch(deleteQualification(record))
  }, [])


  return (
    <Card loading={currentRupState.loading}>
      <Row gutter={[16, 32]}>
        <Col span={24}>
          <Row justify='space-between'>
            <Col>
              <MyButton title={t('tabs.backGeneralRup')} info onClick={handleBackToRups}/>
            </Col>

            <Col>
              <Row gutter={16}>
                <Col>
                  <Tooltip open={!currentRupState.data.isCanDelete} title={t('error.load')} placement='left'>
                    <MyButton title={t('button.deleteRup')} danger onClick={handleDeleteRup}
                              disabled={!currentRupState.data.isCanDelete}/>
                  </Tooltip>
                </Col>

                <Col>
                  <MyButton title={t('tabs.createQualification')} warning onClick={handleCreateRup}/>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>

        <Col span={24} style={{backgroundColor: 'rgba(80,184,95,.2)'}}>
          <Row style={{padding: 18}}>
            <Col span={8}>
              <Row>
                <Col span={24}>
                  <MyText>
                    {t('rupName')}:
                  </MyText>
                  <MyText>
                    {currentRupState?.data?.name}
                  </MyText>
                </Col>

                <Col span={24}>
                  <MyText>
                    {t('studentBase')}:
                  </MyText>
                  <MyText>
                    {RUP_BASE_TYPES().find(rupType => rupType.id === currentRupState?.data?.type)?.caption}
                  </MyText>
                </Col>

                {
                  currentRupState?.data?.linkGroups?.length > 0 &&
                  <Col span={24}>
                    <MyText>
                      {t('groups')}:
                    </MyText>
                    <MyText>
                      {currentRupState?.data?.linkGroups?.map((item: any) => item?.group?.caption)}
                    </MyText>
                  </Col>
                }

              </Row>
            </Col>

            <Col span={8}>
              <Row>
                <Col span={24}>
                  <MyText>
                    {t('table.profile')}:
                  </MyText>
                  <MyText>
                    {currentRupState?.data?.profile?.caption} - {currentRupState?.data?.profile?.code}
                  </MyText>
                </Col>

                <Col span={24}>
                  <MyText>
                    {t('table.profileRoute')}:
                  </MyText>
                  <MyText>
                    {currentRupState?.data?.profile_direction?.caption} - {currentRupState?.data?.profile_direction?.code}
                  </MyText>
                </Col>
              </Row>
            </Col>

            <Col span={8}>
              <Row>
                <Col span={24}>
                  <MyText>
                    {t('table.specialization')}:
                  </MyText>
                  <MyText>
                    {currentRupState?.data?.specialization?.caption} - {currentRupState?.data?.specialization?.code}
                  </MyText>
                </Col>

                <Col span={24}>
                  <MyText>
                    {t('table.specialty')}:
                  </MyText>
                  <MyText>
                    {currentRupState?.data?.speciality?.caption} - {currentRupState?.data?.speciality?.code}
                  </MyText>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Table pagination={false}
                 loading={qualificationsState.loading}
                 bordered
                 columns={columns}
                 dataSource={qualificationsState.data}
                 rowKey={(record: any) => record.id}
                 scroll={{x: true}}/>
        </Col>
      </Row>
    </Card>
  )
}
